import { Container, Sprite } from 'pixi.js';
import { momentAt } from '../../shared/palette';
import type { SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import {
  DUST_LOOKS,
  PUFF_COUNT,
  launchOf,
  puffLift,
  puffOf,
  puffOpacity,
  puffScale,
  riseOf,
} from './dust-filter';
import type { Frame } from '../frame';
import { WHITE, paint } from '../paint';
import { parseHexColor } from '../palette';
import { setTint } from '../util';

const TEXTURE_RADIUS = 16;
const SPREAD = 1.6;

export function createDust(ctx: RenderContext): Family {
  const root = new Container();
  ctx.layers.decor.addChild(root);
  const puff = paint(TEXTURE_RADIUS * 2, TEXTURE_RADIUS * 2, TEXTURE_RADIUS, (canvas) => {
    const gradient = canvas.createRadialGradient(0, 0, 0, 0, 0, TEXTURE_RADIUS);
    gradient.addColorStop(0, WHITE);
    gradient.addColorStop(0.45, 'rgb(255 255 255 / 0.6)');
    gradient.addColorStop(1, 'rgb(255 255 255 / 0)');
    canvas.fillStyle = gradient;
    canvas.fillRect(-TEXTURE_RADIUS, -TEXTURE_RADIUS, TEXTURE_RADIUS * 2, TEXTURE_RADIUS * 2);
  });
  const sprites = Array.from({ length: PUFF_COUNT }, () => {
    const sprite = new Sprite(puff.texture);
    sprite.anchor.set(0.5);
    root.addChild(sprite);
    return sprite;
  });
  const xs = new Float64Array(PUFF_COUNT);
  const ys = new Float64Array(PUFF_COUNT);
  const radii = new Float64Array(PUFF_COUNT);
  const launches = new Float64Array(PUFF_COUNT).fill(Number.NaN);

  let laidSeed = Number.NaN;
  let laidWidth = Number.NaN;
  let laidHeight = Number.NaN;
  let lookedAt = Number.NaN;
  let alpha = 0;

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      root.visible = frame.set.decor === 'pumpui';
      if (!root.visible) {
        return;
      }
      const { seed, arena } = state;
      if (seed !== laidSeed || arena.width !== laidWidth || arena.height !== laidHeight) {
        laidSeed = seed;
        laidWidth = arena.width;
        laidHeight = arena.height;
        launches.fill(Number.NaN);
      }
      if (frame.fraction !== lookedAt) {
        lookedAt = frame.fraction;
        const look = momentAt(DUST_LOOKS, frame.fraction);
        const tone = parseHexColor(look.tone);
        for (const sprite of sprites) {
          setTint(sprite, tone);
        }
        alpha = look.alpha;
      }
      const { now, tempo, calm } = frame;
      for (let index = 0; index < PUFF_COUNT; index += 1) {
        const sprite = sprites[index];
        if (sprite === undefined) {
          continue;
        }
        const launch = launchOf(index, now, tempo, calm);
        if (launch !== launches[index]) {
          launches[index] = launch;
          const { x, y, radius } = puffOf(seed, index, launch, {
            x: arena.width / 2,
            y: arena.height / 2,
          });
          xs[index] = x;
          ys[index] = y;
          radii[index] = radius;
        }
        const rise = riseOf(index, now, tempo, calm);
        sprite.position.set(xs[index] ?? 0, (ys[index] ?? 0) - puffLift(rise));
        const size = ((radii[index] ?? 0) * puffScale(rise)) / TEXTURE_RADIUS;
        sprite.scale.set(size * SPREAD, size);
        sprite.alpha = alpha * puffOpacity(rise);
      }
    },
    destroy(): void {
      root.destroy({ children: true });
      puff.texture.destroy(true);
    },
  };
}
